import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Download, FileDown, ChevronDown, ChevronUp,
   Calendar, Search, X, BadgeCheck, XCircle, 
  Pill, User, Stethoscope, FileText, MessageSquare, Clock
} from 'lucide-react';
import Form from '../components/Form';
import apiService from '../services/api';
import { Prescription, FormField, Medicine, DiagnosisItem, Measurement, InvestigationItem } from '../types/api';
import { downloadPrescriptionsAsPDF } from '../utils/downloadUtils';

interface PrescriptionCardProps {
  prescription: Prescription;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (prescription: Prescription) => void;
  onDelete: (prescription: Prescription) => void;
  onDownload: (prescription: Prescription) => void;
}

const PrescriptionCard: React.FC<PrescriptionCardProps> = ({ 
  prescription, isExpanded, onToggle, onEdit, onDelete, onDownload
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMedicineCount = () => prescription.medicine?.length || 0;

  return (
    <div className="user-card">
      {/* Card Header */}
      <div className="user-card-header" onClick={onToggle}>
        <div className="user-card-left">
          <div className="user-avatar-large bg-gradient-to-br from-rose-500 to-pink-600">
            <Pill size={24} />
          </div>
          <div className="user-card-info">
            <div className="user-card-name">
              Prescription #{prescription.prescription_id?.substring(0, 8) || 'N/A'}
            </div>
            <div className="user-card-meta">
              <span className="user-card-id">
                <User size={12} />
                Patient: {prescription.sbu_id}
              </span>
              <span className="user-card-email">
                <Stethoscope size={12} />
                Doctor: {prescription.sbd_id}
              </span>
            </div>
          </div>
        </div>
        
        <div className="user-card-right">
          <div className="user-card-badges">
            <span className="user-badge badge-medicine">
              <Pill size={12} /> {getMedicineCount()} Medicines
            </span>
            <span className="user-badge badge-date">
              <Calendar size={12} /> {formatDate(prescription.date)}
            </span>
          </div>
          <button className="expand-btn" aria-label="Toggle details">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`user-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="user-details-grid">
          {/* Basic Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <FileText size={16} />
              Prescription Details
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Prescription ID</span>
                <span className="detail-value font-mono text-xs">{prescription.prescription_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Patient ID (SBU)</span>
                <span className="detail-value font-mono">{prescription.sbu_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Doctor ID (SBD)</span>
                <span className="detail-value font-mono">{prescription.sbd_id}</span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Calendar size={16} />
              Dates
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Prescription Date</span>
                <span className="detail-value">{formatDate(prescription.date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Next Visit</span>
                <span className={`detail-value ${prescription.next_date ? 'text-blue-600' : 'text-gray-400'}`}>
                  {prescription.next_date ? formatDate(prescription.next_date) : 'Not scheduled'}
                </span>
              </div>
            </div>
          </div>

          {/* Complaints & Diagnosis */}
          <div className="detail-section" style={{ gridColumn: 'span 2' }}>
            <h4 className="detail-section-title">
              <MessageSquare size={16} />
              Complaints & Diagnosis
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Complaints</span>
                <span className="detail-value">{prescription.complains || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Diagnosis</span>
                <div className="diagnosis-tags">
                  {prescription.diagnosis?.length > 0 ? prescription.diagnosis.map((d: DiagnosisItem, idx: number) => (
                    <span key={idx} className="diagnosis-tag" title={d.snomed_code ? `SNOMED: ${d.snomed_code}` : undefined}>
                      {d.name}
                      {d.since && <span className="diagnosis-since"> (Since: {d.since})</span>}
                      {d.remarks && <span className="diagnosis-remarks"> — {d.remarks}</span>}
                    </span>
                  )) : <span className="text-gray-400">No diagnosis</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Medicines */}
          <div className="detail-section" style={{ gridColumn: 'span 2' }}>
            <h4 className="detail-section-title">
              <Pill size={16} />
              Medicines ({getMedicineCount()})
            </h4>
            <div className="medicines-list">
              {prescription.medicine?.map((med: Medicine, idx: number) => (
                <div key={idx} className="medicine-item">
                  <span className="medicine-name">{med.name}</span>
                  <span className="medicine-dosage">{med.dosage}</span>
                  <span className="medicine-timing">{med.timings}</span>
                  {med.when && <span className="medicine-when">When: {med.when}</span>}
                  {med.administration && <span className="medicine-admin">Route: {med.administration}</span>}
                  {med.duration && <span className="medicine-duration">Duration: {med.duration}</span>}
                  {med.comments && <span className="medicine-comments">{med.comments}</span>}
                </div>
              ))}
              {(!prescription.medicine || prescription.medicine.length === 0) && (
                <div className="question-empty">No medicines prescribed</div>
              )}
            </div>
          </div>

          {/* Measurements */}
          {prescription.measurements && prescription.measurements.length > 0 && (
            <div className="detail-section">
              <h4 className="detail-section-title">
                <Clock size={16} />
                Measurements
              </h4>
              <div className="measurements-list">
                {prescription.measurements.map((m: Measurement, idx: number) => (
                  <div key={idx} className="measurement-item">
                    <span className="measurement-name">{m.name}</span>
                    <span className="measurement-value">{m.value} {m.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advice */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <MessageSquare size={16} />
              Medical Advice
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-value">{prescription.advice || 'No specific advice'}</span>
              </div>
            </div>
          </div>

          {/* Investigations */}
          {prescription.investigation && prescription.investigation.length > 0 && (
            <div className="detail-section" style={{ gridColumn: 'span 2' }}>
              <h4 className="detail-section-title">
                <Stethoscope size={16} />
                Investigations
              </h4>
              <div className="investigations-list">
                {prescription.investigation.map((inv: InvestigationItem, idx: number) => (
                  <span key={idx} className="investigation-tag">{inv.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="user-card-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(prescription); }}
            className="action-btn action-edit"
            title="Edit Prescription"
          >
            <Edit size={16} />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(prescription); }}
            className="action-btn action-download"
            title="Download as PDF"
          >
            <Download size={16} />
            Download PDF
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(prescription); }}
            className="action-btn action-delete"
            title="Delete Prescription"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Prescriptions: React.FC = () => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState<Prescription | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [expandedPrescriptions, setExpandedPrescriptions] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllPrescriptions();
      setPrescriptions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (prescriptionId: string) => {
    setExpandedPrescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(prescriptionId)) {
        newSet.delete(prescriptionId);
      } else {
        newSet.add(prescriptionId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedPrescriptions(new Set(filteredPrescriptions.map((p, i) => p.prescription_id || `idx-${i}`)));
  };

  const collapseAll = () => {
    setExpandedPrescriptions(new Set());
  };

  const handleDeletePrescription = async (prescription: Prescription) => {
    if (window.confirm('Are you sure you want to delete this prescription?')) {
      try {
        await apiService.deletePrescription(prescription.prescription_id!);
        setSuccess('Prescription deleted successfully');
        await fetchPrescriptions();
      } catch (err: any) {
        setError(err.message || 'Failed to delete prescription');
      }
    }
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      setFormLoading(true);
      if (editingPrescription) {
        const updateData = {
          medication: formData.medication,
          dosage: formData.dosage,
          frequency: formData.frequency
        };
        await apiService.updatePrescription(editingPrescription.prescription_id!, updateData);
        setSuccess('Prescription updated successfully');
      } else {
        await apiService.createPrescription(formData as Prescription);
        setSuccess('Prescription created successfully');
      }
      setShowForm(false);
      setEditingPrescription(null);
      await fetchPrescriptions();
    } catch (err: any) {
      setError(err.message || 'Failed to save prescription');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownloadAllPDF = async () => {
    try {
      await downloadPrescriptionsAsPDF(prescriptions, 'prescriptions');
      setSuccess('All prescriptions exported successfully!');
    } catch (error) {
      setError('Failed to download prescriptions');
    }
  };

  const handleDownloadIndividual = async (prescription: Prescription) => {
    try {
      await downloadPrescriptionsAsPDF([prescription], `prescription-${prescription.prescription_id}`);
      setSuccess('Prescription downloaded successfully!');
    } catch (error) {
      setError('Failed to download prescription');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPrescription(null);
  };

  const prescriptionFormFields: FormField[] = [
    {
      name: 'sbu_id',
      label: 'User ID (SBU)',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'sbd_id',
      label: 'Doctor ID (SBD)',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'complains',
      label: 'Complaints',
      type: 'textarea',
      required: true,
      validation: { minLength: 10, maxLength: 500 }
    },
    {
      name: 'advice',
      label: 'Medical Advice',
      type: 'textarea',
      required: true,
      validation: { minLength: 10, maxLength: 500 }
    },
    {
      name: 'date',
      label: 'Prescription Date',
      type: 'date',
      required: true
    },
    {
      name: 'next_date',
      label: 'Next Visit Date',
      type: 'date',
      required: false
    }
  ];

  const updateFormFields: FormField[] = [
    {
      name: 'medication',
      label: 'Medication',
      type: 'text',
      required: false,
      validation: { maxLength: 100 }
    },
    {
      name: 'dosage',
      label: 'Dosage',
      type: 'text',
      required: false,
      validation: { maxLength: 50 }
    },
    {
      name: 'frequency',
      label: 'Frequency',
      type: 'text',
      required: false,
      validation: { maxLength: 50 }
    }
  ];

  // Filter prescriptions
  const filteredPrescriptions = prescriptions.filter(prescription => {
    if (searchQuery === '') return true;
    const q = searchQuery.toLowerCase();
    return (
      prescription.prescription_id?.toLowerCase().includes(q) ||
      prescription.sbu_id.toLowerCase().includes(q) ||
      prescription.sbd_id.toLowerCase().includes(q) ||
      prescription.complains?.toLowerCase().includes(q) ||
      prescription.diagnosis?.some(d => d.name?.toLowerCase().includes(q))
    );
  });

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Prescriptions Management</h2>
          <p className="users-subtitle">Manage medical prescriptions • {prescriptions.length} total prescriptions</p>
        </div>
        <div className="flex gap-2">
          {prescriptions.length > 0 && (
            <button onClick={handleDownloadAllPDF} className="btn btn-secondary">
              <FileDown size={16} />
              Export All
            </button>
          )}
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <Plus size={16} />
            Add Prescription
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <XCircle size={18} />
          {error}
          <button onClick={() => setError(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          <BadgeCheck size={18} />
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCancelForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingPrescription ? <Edit size={20} /> : <Plus size={20} />}
                {editingPrescription ? 'Edit Prescription' : 'Add New Prescription'}
              </h3>
              <button onClick={handleCancelForm} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <Form
                fields={editingPrescription ? updateFormFields : prescriptionFormFields}
                onSubmit={handleFormSubmit}
                loading={formLoading}
                initialData={editingPrescription || {}}
                submitText={editingPrescription ? 'Update Prescription' : 'Create Prescription'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="users-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by ID, patient, doctor, or complaints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filters-group">
          <div className="expand-controls">
            <button onClick={expandAll} className="expand-btn-small" title="Expand All">
              <ChevronDown size={16} /> Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn-small" title="Collapse All">
              <ChevronUp size={16} /> Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Showing {filteredPrescriptions.length} of {prescriptions.length} prescriptions
      </div>

      {/* Prescription Cards */}
      <div className="users-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading prescriptions...</p>
          </div>
        ) : filteredPrescriptions.length === 0 ? (
          <div className="empty-state">
            <Pill size={48} className="text-gray-300" />
            <h3>No prescriptions found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : (
          filteredPrescriptions.map((prescription, index) => {
            const cardId = prescription.prescription_id || `idx-${index}`;
            return (
              <PrescriptionCard
                key={cardId}
                prescription={prescription}
                isExpanded={expandedPrescriptions.has(cardId)}
                onToggle={() => toggleExpanded(cardId)}
                onEdit={(p) => { setEditingPrescription(p); setShowForm(true); }}
                onDelete={handleDeletePrescription}
                onDownload={handleDownloadIndividual}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default Prescriptions;
